import { ActionIcon, Box, Button, Checkbox, LoadingOverlay, Text } from '@mantine/core'
import { ColumnDef, Row } from '@tanstack/react-table'
import { Loading } from 'components/loading/loading'
import { Table } from 'components/table'
import { BatchPrintConfirmation } from 'components/modal/batch-print-confirmation'
import { useTranslation } from 'react-i18next'
import { IconPlus, IconPrinter } from '@tabler/icons-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useUsers } from 'api/query/user'
import { User } from 'api/entities/user'
import { download, saveBase64ToFile } from 'utils/base64'
import dayjs from 'dayjs'
import { showNotification } from '@mantine/notifications'
import { getBadge, PrintSource, useBatchPrintUsersBadge } from 'utils/badge'

export function Users() {
  const { t } = useTranslation()
  const { tradeshowId } = useParams()
  const { data: users, isLoading: isLoadingUsers } = useUsers(tradeshowId)
  const { mutateAsync: batchPrintUserBadge } = useBatchPrintUsersBadge(tradeshowId)

  const [isPrinting, setIsPrinting] = useState<string | undefined>()
  const [isBatchPrinting, setIsBatchPrinting] = useState(false)
  const [confirmationOpened, setConfirmationOpened] = useState(false)
  const [pendingBatchPrint, setPendingBatchPrint] = useState<Row<User>[]>([])

  const handleBatchPrint = async (rows: Row<User>[]) => {
    setIsBatchPrinting(true)
    showNotification({
      message: 'Downloading badges... Please wait',
      color: 'green',
      title: 'Downloading...',
      loading: true,
      autoClose: 5000,
    })

    try {
      const url = await batchPrintUserBadge(rows.map((r) => r.original.id))
      download(`users-badges-${dayjs().format('YYYY-MM-DD-HH-mm')}`, url)
    } catch (error) {
      // nothing
    }

    setIsBatchPrinting(false)
  }

  const actionButtons = (rows: Row<User>[]) => [
    <Button
      ml={5}
      color="custom-primary-color.0"
      h={44}
      disabled={isBatchPrinting}
      leftIcon={<IconPrinter size={17} />}
      size="sm"
      onClick={async () => {
        if (rows.length > 5) {
          setPendingBatchPrint(rows)
          setConfirmationOpened(true)
        } else {
          await handleBatchPrint(rows)
        }
      }}
    >
      {t('exhibitor.print-all')}
    </Button>,
  ]

  const columns: ColumnDef<User>[] = [
    {
      id: 'print',
      cell: (props) => (
        <Box>
          <ActionIcon
            variant="outline"
            size="md"
            loading={isPrinting === props.row.original.id}
            onClick={() => {
              if (!tradeshowId) {
                return
              }

              setIsPrinting(props.row.original.id)
              setTimeout(async () => {
                showNotification({
                  message: 'Downloading badge... Please wait',
                  color: 'green',
                  title: 'Downloading...',
                  loading: true,
                  autoClose: 5000,
                })

                let badge: string | null
                try {
                  badge = await getBadge(tradeshowId, props.row.original.code, PrintSource.ADMIN)
                } catch (error) {
                  setIsPrinting(undefined)
                  return
                }

                if (!badge) {
                  setIsPrinting(undefined)
                  return
                }

                saveBase64ToFile(badge, `exhibitor-badge-${props.row.original.id}.png`)
                setIsPrinting(undefined)
              }, 400)
            }}
          >
            <IconPrinter size="20px" />
          </ActionIcon>
        </Box>
      ),
    },
    {
      id: 'printed',
      header: t('exhibitor.printed'),
      accessorKey: 'lastPrintedAt',
      minSize: 70,
      cell: (props) => {
        if (!props.getValue()) {
          return <Checkbox size="xs" checked={false} />
        }

        return <Checkbox size="xs" checked />
      },
    },
    {
      id: 'created',
      header: t('exhibitor.created-at'),
      accessorKey: 'created',
      enableColumnFilter: false,
      cell: (props) => {
        return <Text size="10px">{dayjs(props.getValue() as string).format('YYYY-MM-DD HH:mm')}</Text>
      },
      minSize: 100,
    },
    {
      id: 'company-name',
      header: t('exhibitor.company-name'),
      accessorKey: 'companyName',
      minSize: 100,
    },
    {
      id: 'fullName',
      header: t('exhibitor.fullName'),
      accessorKey: 'fullName',
      minSize: 100,
    },
    {
      id: 'category',
      accessorKey: 'category',
      header: t('exhibitor.category'),
      minSize: 100,
    },
    {
      id: 'email',
      header: t('exhibitor.email'),
      accessorKey: 'email',
      enableColumnFilter: false,
      cell: (props) => (props.getValue() as string).toLocaleLowerCase(),
      minSize: 100,
    },
  ]

  if (isLoadingUsers) {
    return <Loading height="calc(100vh - 235px)" size="md" />
  }

  return (
    <>
      <LoadingOverlay visible={isPrinting !== undefined} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <Table<User>
        defaultSort={{
          id: 'created',
          desc: true,
        }}
        data={users || []}
        columns={columns}
        actionButtons={actionButtons}
        exportTable={{ filename: `users-${dayjs().format('YYYY-MM-DD-HH-mm')}` }}
      />

      <BatchPrintConfirmation
        opened={confirmationOpened}
        onClose={() => setConfirmationOpened(false)}
        onConfirm={async () => {
          setConfirmationOpened(false)
          await handleBatchPrint(pendingBatchPrint)
          setPendingBatchPrint([])
        }}
        count={pendingBatchPrint.length}
        entityType="user"
      />
    </>
  )
}
